/* eslint-disable no-unused-vars */
import { memo, useEffect, useState } from 'react';
import { accountSettingsAPI } from 'services/accountSettings.service';
import { showPolipop } from 'utils';
import { Palette } from 'config';
import styled from 'styled-components';
import {
  CustomInput,
  ModalBase,
  ModalHeader,
  ModalFooter,
  CustomButton,
  Message,
  Loader,
} from 'components';
import { CloseForm, CheckForm, Eye, EyeOff, ArrowUp } from 'assets/icons';
import { useForm, Controller } from 'react-hook-form';

const inputWidth = 385;

const Box = styled.div`
  height: 100px;
`;

const passValidation = [
  { title: 'At least one upper case english letter', pattern: '^(?=.*[A-Z])' },
  { title: 'At least one lower case english letter', pattern: '^(?=.*[a-z])' },
  { title: 'At least one digit', pattern: '^(?=.*[0-9])' },
  { title: 'At least one special character', pattern: '[^A-Za-z0-9]' },
  { title: 'Minimum 8 in length', pattern: '^.{8,}$' },
];

const validatePassword = (value, formValues) => {
  if (value === formValues.oldPassword)
    return 'You cannot reuse your current or recently used password.';

  for (const { title, pattern } of passValidation) {
    const regex = new RegExp(pattern);
    if (!regex.test(value)) return title;
  }
  return true;
};

const ChangePasswordSection = () => {
  const {
    reset,
    handleSubmit,
    setValue,
    getValues,
    control,
    trigger,
    setError,
    watch,
    clearErrors,
    formState: { isDirty, errors, isValid, isSubmitting, isSubmitSuccessful },
  } = useForm({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [saveNewPassword, result] = accountSettingsAPI.useSaveAccountSettingsPasswordMutation();

  useEffect(() => {
    if (result && result.isError) {
      setError('oldPassword', { type: 'custom', message: result.error.data.Message });
    }
    if (result && result.isSuccess) {
      showPolipop(result);
      setShowPasswordForm(false);
    }
  }, [result]);

  const handleSavePassword = async () => {
    const { oldPassword, newPassword } = getValues();
    const res = await saveNewPassword({ oldPassword, newPassword });
  };

  const newPasswordValue = watch('newPassword');
  const oldPasswordValue = watch('oldPassword');

  useEffect(() => {
    const { confirmPassword } = getValues();
    if (confirmPassword) trigger('confirmPassword');
  }, [newPasswordValue]);

  useEffect(() => {
    if (newPasswordValue) trigger('newPassword');
  }, [oldPasswordValue]);

  const onCancel = () => {
    setShowPasswordForm(false);
    reset();
  };

  return (
    <>
      {result.isLoading && <Loader.Global delay='1000' />}
      <CustomButton
        type='button'
        id='open_password'
        buttonColor={CustomButton.Colors.Secondary}
        onClick={() => setShowPasswordForm(true)}
      >
        Reset Password
      </CustomButton>
      {showPasswordForm ? (
        <ModalBase>
          <div
            style={{
              padding: '16px 24px',
            }}
          >
            <ModalHeader title='Password' close={onCancel} />
          </div>
          <div
            style={{
              padding: '0 48px 0 48px',
              display: 'flex',
              gap: '24px',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Controller
              name='oldPassword'
              control={control}
              rules={{
                required: 'Old Password is required',
              }}
              render={(props) => {
                const { field, fieldState, formState } = props;
                const [showPassword, setShowPassword] = useState(false);
                const errors = formState.errors;
                return (
                  <Box>
                    <CustomInput
                      topLabel='Old password:'
                      type={showPassword ? 'text' : 'password'}
                      value={field.value || ''}
                      autoComplete='off'
                      id={field.name}
                      rightIcon={showPassword ? <EyeOff /> : <Eye />}
                      rightIconOnClick={() => setShowPassword((prev) => !prev)}
                      name={field.name}
                      error={Boolean(errors?.oldPassword)}
                      errorText={errors?.oldPassword?.message}
                      onChange={field.onChange}
                      width={inputWidth}
                    />
                  </Box>
                );
              }}
            />
            <Controller
              name='newPassword'
              control={control}
              rules={{
                required: 'Password is required',
                validate: validatePassword,
              }}
              render={(props) => {
                const { field, fieldState, formState } = props;
                const [showPassword, setShowPassword] = useState(false);
                const errors = formState.errors;
                const errorMessage =
                  errors?.newPassword?.message ===
                  'You cannot reuse your current or recently used password.'
                    ? 'You cannot reuse your current or recently used password.'
                    : '';
                return (
                  <div>
                    <CustomInput
                      type={showPassword ? 'text' : 'password'}
                      topLabel='New password:'
                      value={field.value}
                      autoComplete='off'
                      id={field.name}
                      rightIcon={showPassword ? <EyeOff /> : <Eye />}
                      rightIconOnClick={() => setShowPassword((prev) => !prev)}
                      name={field.name}
                      error={Boolean(errors?.newPassword)}
                      errorText={errorMessage}
                      onChange={field.onChange}
                      width={inputWidth}
                    />
                    <div style={{ marginTop: '5px' }}>
                      {passValidation.map((item) => {
                        const pattern = new RegExp(item.pattern);
                        const isNotEmpty = field.value?.length > 0;
                        const isComplete = pattern.test(field.value);
                        return (
                          <ValidationItem
                            isRed={isNotEmpty && !isComplete}
                            isGreen={isNotEmpty && isComplete}
                            key={item.title}
                          >
                            {isNotEmpty && !isComplete && <CloseForm />}
                            {isNotEmpty && isComplete && <CheckForm />}
                            {!isNotEmpty && (
                              <IconWrap>
                                <ArrowUp />
                              </IconWrap>
                            )}
                            {item.title}
                          </ValidationItem>
                        );
                      })}
                    </div>
                  </div>
                );
              }}
            />
            <Controller
              name='confirmPassword'
              control={control}
              rules={{
                required: 'Password is required',
                validate: (value, formValues) => {
                  return (
                    value === formValues.newPassword ||
                    'Confirm password field does not match new password'
                  );
                },
              }}
              render={(props) => {
                const { field, fieldState, formState } = props;
                const [showPassword, setShowPassword] = useState(false);
                const errors = formState.errors;
                return (
                  <Box>
                    <CustomInput
                      type={showPassword ? 'text' : 'password'}
                      topLabel='Confirm new password:'
                      value={field.value}
                      autoComplete='off'
                      id={field.name}
                      rightIcon={showPassword ? <EyeOff /> : <Eye />}
                      rightIconOnClick={() => setShowPassword((prev) => !prev)}
                      name={field.name}
                      error={Boolean(errors?.confirmPassword)}
                      errorText={errors?.confirmPassword?.message ?? ''}
                      onChange={field.onChange}
                      width={inputWidth}
                    />
                  </Box>
                );
              }}
            />
          </div>
          <ModalFooter
            saveButtonType='button'
            saveButtonText=' Save new password'
            saveButtonOnClick={handleSavePassword}
            close={onCancel}
            saveButtonDisabled={!isDirty || !isValid}
          />
        </ModalBase>
      ) : null}
    </>
  );
};

export default memo(ChangePasswordSection);

const ValidationItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ isRed, isGreen }) =>
    isRed
      ? `${Palette.Red._600}`
      : isGreen
      ? `${Palette.Teal._600}`
      : `${Palette.PrimaryGray._500}`};
  margin-left: 4px;
  font-size: 12px;
  line-height: 16px;
  transition: all 0.4s ease-in-out;

  svg {
    fill: ${({ isRed, isGreen }) =>
      isRed
        ? `${Palette.Red._600}`
        : isGreen
        ? `${Palette.Teal._500}`
        : `${Palette.PrimaryGray._500}`};
    transition: all 0.4s ease-in-out;
  }
`;

const IconWrap = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  transform: translate(-2px, -2px) rotate(90deg);

  svg {
    width: 9px;
    height: 6px;
  }
`;
